import { useIntlTranslation } from "gatsby-plugin-intl-sushishop";
import htmlParse from "html-react-parser";
import { useEffect, useRef } from "react";
import { vsprintf } from "sprintf-js";
import { dataDogLogger } from "@src/lib/datadog/initDatadogLogs";
export interface IPluralArgs {
  count: number;
}
export interface ITranslationOptions {
  vsprintfArgs?: any[];
  plural?: IPluralArgs;
}

/* plural example
  {t( "{count, plural, =0 {no items} one {# item} other {# items}}", "StoreLocSeo", { plural: { count: 1 } } )}
 */

// Séparateur interne pour encoder (namespace, clé) dans un Set de candidats.
// Caractère nul : ne peut apparaître ni dans un namespace ni dans une clé
// (une clé peut contenir des espaces, ex. messages ICU pluriels).
const CANDIDATE_SEP = " ";

// Dédup global (durée de vie de la session) : une clé manquante n'est
// remontée à DataDog qu'une seule fois, tous composants confondus.
const reportedMissingKeys = new Set<string>();

export const useTranslation = (ns: string | string[]) => {
  const { formatMessage, messages, locale } = useIntlTranslation(ns);

  // Clés potentiellement manquantes détectées pendant le rendu.
  // Vérifiées en effet (après chargement lazy des namespaces) pour
  // éviter les faux positifs dûs au fetch asynchrone des traductions.
  const missingCandidates = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (missingCandidates.current.size === 0) return;

    // Namespaces réellement chargés : au moins une clé "namespace:xxx"
    // présente dans les messages implique que le JSON du namespace est arrivé.
    const loadedNamespaces = new Set<string>();
    Object.keys(messages).forEach((key) => {
      const idx = key.indexOf(":");
      if (idx > 0) loadedNamespaces.add(key.slice(0, idx));
    });

    missingCandidates.current.forEach((candidate) => {
      const [context, translationKey] = candidate.split(CANDIDATE_SEP);
      const id = `${context}:${translationKey}`;

      // Résolue entre-temps (namespace chargé depuis) : on abandonne le candidat.
      if (messages[id] !== undefined) {
        missingCandidates.current.delete(candidate);
        return;
      }

      // Namespace chargé mais clé toujours absente => réellement manquante.
      if (loadedNamespaces.has(context)) {
        missingCandidates.current.delete(candidate);

        const dedupKey = `${locale}:${id}`;
        if (!reportedMissingKeys.has(dedupKey) && locale !== "fr") {
          // le français est la langue par défaut des clés, il faut donc l'ignorer
          // dans le tracking des clés manquantes
          reportedMissingKeys.add(dedupKey);
          dataDogLogger.warn(
            `Missing translation ${locale} in \`${context}\` for key \`${translationKey}\``,
            {
              key: translationKey,
              context,
              locale,
              fn: "t",
            }
          );
        }
      }
      // Sinon : namespace pas encore chargé, on conserve le candidat
      // pour re-vérification au prochain changement de `messages`.
    });
  }, [messages, locale]);

  const self = {
    /**
     *
     * @param translationKey Translation key
     * @param context Translation context
     * @param options Extra options to the translation function (plural, vsprintfArgs...)
     * @returns The translated text as string
     */
    t: (
      translationKey: string,
      context: string,
      options?: ITranslationOptions
    ): string => {
      const { vsprintfArgs, plural } = options || {};

      // Détection légère : si l'id n'est pas (encore) dans les messages,
      // on l'enregistre comme candidat. La confirmation se fait en effet.
      if (messages[`${context}:${translationKey}`] === undefined) {
        missingCandidates.current.add(
          `${context}${CANDIDATE_SEP}${translationKey}`
        );
      }

      // translation process
      let translation: string = formatMessage(
        {
          id: `${context}:${translationKey}`,
          defaultMessage: translationKey,
        },
        plural ? { count: plural.count } : undefined
      );

      // handle vsprintf args
      if (vsprintfArgs) {
        translation = vsprintf(translation, vsprintfArgs);
      }

      return translation;
    },
    /**
     *
     * @param translationKey Translation key
     * @param context Translation context
     * @param options Extra options to the translation function (plural, vsprintfArgs...)
     * @returns The translated text or JSX Element (string or JSX.Element or JSX.Element[])
     */
    tHtml: (
      translationKey: string,
      context: string,
      options?: ITranslationOptions
    ): string | JSX.Element | JSX.Element[] =>
      htmlParse(self.t(translationKey, context, options)),
  };
  return self;
};
